// @file Surface custom property responses composable (ratings and votes)
import { captureFetchException } from '@@/bits/error_tracker'
import { __ } from '@@/bits/intl'
import { getCustomPropertyValue, isWallCustomPostPropertyOfType, setCustomPropertyValue } from '@@/bits/post_properties'
import { HttpCode, SnackbarNotificationType } from '@@/enums'
import { useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { useSurfaceGuestStore } from '@@/pinia/surface_guest_store'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useSurfacePostPropertiesStore } from '@@/pinia/surface_post_properties'
import type {
  DeleteCustomPropertyResponsePayload,
  UpsertCustomPropertyResponsePayload,
} from '@@/surface/api/custom_property_response'
import { CustomPropertyResponseApi } from '@@/surface/api/custom_property_response'
import { PostAttributes, WallCustomPostProperty } from '@@/types'
import type { FetchError } from '@padlet/fetch'
import { applyResponseToFieldValue } from '@padlet/universal-post-editor/src/bits/custom_property_responses'

interface UpdateAggregatedCustomPropertyResponseRealtimePayload {
  wish_id: number
  custom_property_id: string
  sum: number
  count: number
  user_hashid: string
}

interface UpdateUserCustomPropertyResponseRealtimePayload {
  wish_id: number
  custom_property_id: string
  int_value: number
}

interface DeleteUserCustomPropertyResponseRealtimePayload {
  id: number
  wish_id: number
  custom_property_id: string
}

const DELETE_CUSTOM_PROPERTY_RESPONSE_SNACKBAR_MESSAGES: Partial<
  Record<WallCustomPostProperty['data_type'] | 'default', string>
> = {
  vote: __('This vote cannot be removed because the post cannot be empty.'),
  rating: __('This rating cannot be removed because the post cannot be empty.'),
  score: __('This score cannot be removed because the post cannot be empty.'),
  default: __('This value cannot be removed because the post cannot be empty.'),
}

const useSurfaceCustomPropertyResponse = (): {
  createOrUpdateCustomPropertyResponse: typeof createOrUpdateCustomPropertyResponse
  deleteCustomPropertyResponse: typeof deleteCustomPropertyResponse
  updateAggregatedCustomPropertyResponseRealtime: typeof updateAggregatedCustomPropertyResponseRealtime
  updateUserCustomPropertyResponseRealtime: typeof updateUserCustomPropertyResponseRealtime
  deleteUserCustomPropertyResponseRealtime: typeof deleteUserCustomPropertyResponseRealtime
  mergeRealtimeCustomPropertiesPayload: typeof mergeRealtimeCustomPropertiesPayload
} => {
  const globalSnackbarStore = useGlobalSnackbarStore()
  const surfaceGuestStore = useSurfaceGuestStore()
  const surfacePostsStore = useSurfacePostsStore()
  const surfacePostPropertiesStore = useSurfacePostPropertiesStore()

  const createOrUpdateCustomPropertyResponse = async (payload: UpsertCustomPropertyResponsePayload): Promise<void> => {
    // Get the post by hashid
    const post = Object.values(surfacePostsStore.postEntitiesById).find((p) => p?.hashid === payload.wishHashId)
    if (post == null) {
      void globalSnackbarStore.genericFetchError()
      return
    }

    // Check if guest needs to identify
    if (surfaceGuestStore.shouldShowGuestIdModal) {
      surfaceGuestStore.showGuestIdModal({
        afterSaveActions: [async () => await createOrUpdateCustomPropertyResponse(payload)],
      })
      return
    } else if (surfaceGuestStore.shouldUpdateNameBeforePublishing) {
      surfaceGuestStore.setUserName(surfaceGuestStore.displayName)
      await surfaceGuestStore.updateSessionUserName({
        shouldModerate: false,
      })
    }

    const { customPropertyId } = payload

    const wallCustomProperty = surfacePostPropertiesStore.wallCustomPostPropertiesById[customPropertyId]
    if (
      !isWallCustomPostPropertyOfType(wallCustomProperty, 'vote') &&
      !isWallCustomPostPropertyOfType(wallCustomProperty, 'rating') &&
      !isWallCustomPostPropertyOfType(wallCustomProperty, 'score')
    ) {
      return
    }

    const oldFieldValue = getCustomPropertyValue(post, wallCustomProperty)
    const newFieldValue = applyResponseToFieldValue(oldFieldValue, payload.value)

    try {
      surfacePostsStore.updatePostInStore({
        ...post,
        custom_properties: {
          ...post.custom_properties,
          [customPropertyId]: newFieldValue,
        },
      })

      await CustomPropertyResponseApi.upsert(payload)
    } catch (error) {
      // Revert the optimistic update
      if (post.id) {
        surfacePostsStore.updatePostInStore({
          ...post,
          custom_properties: {
            ...post.custom_properties,
            [customPropertyId]: oldFieldValue,
          },
        })
      }

      if (error?.status === HttpCode.Forbidden) {
        globalSnackbarStore.setSnackbar({
          message: __('You do not have permission to respond to this custom field.'),
          notificationType: SnackbarNotificationType.error,
        })
      } else {
        globalSnackbarStore.genericFetchError()
        captureFetchException(error as FetchError, {
          source: 'createOrUpdateCustomPropertyResponse',
        })
      }
    }
  }

  const deleteCustomPropertyResponse = async (payload: DeleteCustomPropertyResponsePayload): Promise<void> => {
    // Get the post by hashid
    const post = Object.values(surfacePostsStore.postEntitiesById).find((p) => p?.hashid === payload.wishHashId)
    if (post == null) {
      void globalSnackbarStore.genericFetchError()
      return
    }

    const { customPropertyId } = payload

    const wallCustomProperty = surfacePostPropertiesStore.wallCustomPostPropertiesById[customPropertyId]
    if (
      !isWallCustomPostPropertyOfType(wallCustomProperty, 'vote') &&
      !isWallCustomPostPropertyOfType(wallCustomProperty, 'rating') &&
      !isWallCustomPostPropertyOfType(wallCustomProperty, 'score')
    ) {
      return
    }

    // Store original values for revert
    const oldFieldValue = getCustomPropertyValue(post, wallCustomProperty)
    const newFieldValue = applyResponseToFieldValue(oldFieldValue, undefined)

    try {
      surfacePostsStore.updatePostInStore({
        ...post,
        custom_properties: {
          ...post.custom_properties,
          [customPropertyId]: newFieldValue,
        },
      })

      await CustomPropertyResponseApi.delete({ wishHashId: payload.wishHashId, customPropertyId })
    } catch (error) {
      // Revert the optimistic update
      if (post.id) {
        surfacePostsStore.updatePostInStore({
          ...post,
          custom_properties: {
            ...post.custom_properties,
            [customPropertyId]: oldFieldValue,
          },
        })
      }

      if (error?.status === HttpCode.Forbidden) {
        globalSnackbarStore.setSnackbar({
          message: __('You do not have permission to respond to this custom field.'),
          notificationType: SnackbarNotificationType.error,
        })
      } else if (error?.status === HttpCode.UnprocessableEntity && error?.message?.includes('Post cannot be empty')) {
        const wallCustomProperty = surfacePostPropertiesStore.wallCustomPostPropertiesById[payload.customPropertyId]
        const snackbarMessage =
          DELETE_CUSTOM_PROPERTY_RESPONSE_SNACKBAR_MESSAGES[wallCustomProperty?.data_type] ??
          (DELETE_CUSTOM_PROPERTY_RESPONSE_SNACKBAR_MESSAGES.default as string)

        globalSnackbarStore.setSnackbar({
          message: snackbarMessage,
          notificationType: SnackbarNotificationType.error,
        })
      } else {
        globalSnackbarStore.genericFetchError()
        captureFetchException(error as FetchError, {
          source: 'deleteCustomPropertyResponse',
        })
      }
    }
  }

  // #region handlers for realtime updates
  const updateAggregatedCustomPropertyResponseRealtime = async (
    payload: UpdateAggregatedCustomPropertyResponseRealtimePayload,
  ) => {
    const { wish_id: wishId, custom_property_id: customPropertyId, sum, count } = payload
    const post = surfacePostsStore.postEntitiesById[wishId]

    if (post == null) return

    const wallProperty = surfacePostPropertiesStore.wallCustomPostPropertiesById[customPropertyId]
    if (
      !isWallCustomPostPropertyOfType(wallProperty, 'rating') &&
      !isWallCustomPostPropertyOfType(wallProperty, 'score') &&
      !isWallCustomPostPropertyOfType(wallProperty, 'vote')
    ) {
      return
    }

    const existingValue = getCustomPropertyValue(post, wallProperty)

    surfacePostsStore.updatePostInStore({
      id: wishId,
      ...setCustomPropertyValue(post, wallProperty, { ...existingValue, ...{ sum, count } }),
    })
  }

  const updateUserCustomPropertyResponseRealtime = async (payload: UpdateUserCustomPropertyResponseRealtimePayload) => {
    const { wish_id: wishId, custom_property_id: customPropertyId, int_value: intValue } = payload
    const post = surfacePostsStore.postEntitiesById[wishId]

    if (post == null) return

    const wallProperty = surfacePostPropertiesStore.wallCustomPostPropertiesById[customPropertyId]

    if (
      !isWallCustomPostPropertyOfType(wallProperty, 'rating') &&
      !isWallCustomPostPropertyOfType(wallProperty, 'score') &&
      !isWallCustomPostPropertyOfType(wallProperty, 'vote')
    ) {
      return
    }

    const existingValue = getCustomPropertyValue(post, wallProperty)

    surfacePostsStore.updatePostInStore({
      id: wishId,
      ...setCustomPropertyValue(post, wallProperty, { ...existingValue, user_response: intValue }),
    })
  }

  const deleteUserCustomPropertyResponseRealtime = async (payload: DeleteUserCustomPropertyResponseRealtimePayload) => {
    const { wish_id: wishId, custom_property_id: customPropertyId } = payload
    const post = surfacePostsStore.postEntitiesById[wishId]

    if (post == null) return

    const wallProperty = surfacePostPropertiesStore.wallCustomPostPropertiesById[customPropertyId]
    if (
      !isWallCustomPostPropertyOfType(wallProperty, 'rating') &&
      !isWallCustomPostPropertyOfType(wallProperty, 'score') &&
      !isWallCustomPostPropertyOfType(wallProperty, 'vote')
    ) {
      return
    }

    const existingValue = getCustomPropertyValue(post, wallProperty)
    if (existingValue == null) return
    const { user_response: _, ...valueWithoutUserResponse } = existingValue
    surfacePostsStore.updatePostInStore({
      id: wishId,
      ...setCustomPropertyValue(post, wallProperty, valueWithoutUserResponse),
    })
  }

  /**
   * Intelligently merges custom properties from edit_wish realtime events.
   *
   * Problem: The backend doesn't include user_response in edit_wish broadcasts because it's
   * user-specific data (sent separately via update_user_custom_property_response events).
   * Without merging, the frontend would blindly replace custom_properties and lose the user's
   * vote/rating responses whenever any wish update occurs.
   *
   * Solution: For vote/rating properties, preserve user_response from existing state while
   * accepting updates to aggregated data (sum, count). For other property types, replace entirely.
   *
   * @param options.postId - The post ID to look up existing custom properties
   * @param options.customProperties - Incoming custom properties from edit_wish event
   * @returns Merged custom properties with user_response preserved for vote/rating properties
   */
  function mergeRealtimeCustomPropertiesPayload(options: {
    postId: PostAttributes['id']
    customProperties: PostAttributes['custom_properties']
  }): PostAttributes['custom_properties'] {
    const { postId, customProperties: newCustomProperties } = options

    if (!postId) {
      return newCustomProperties
    }

    const post = surfacePostsStore.postEntitiesById[postId]

    if (!post) {
      return newCustomProperties
    }

    const oldCustomProperties = post.custom_properties

    if (!oldCustomProperties) return newCustomProperties

    if (!newCustomProperties) return oldCustomProperties

    const merged = { ...oldCustomProperties }

    Object.keys(newCustomProperties).forEach((propId) => {
      const wallCustomPostProperty = surfacePostPropertiesStore.wallCustomPostPropertiesById[propId]

      if (!wallCustomPostProperty) return

      const isVoteOrRatingProperty =
        isWallCustomPostPropertyOfType(wallCustomPostProperty, 'vote') ||
        isWallCustomPostPropertyOfType(wallCustomPostProperty, 'rating') ||
        isWallCustomPostPropertyOfType(wallCustomPostProperty, 'score')

      if (isVoteOrRatingProperty) {
        const newValue = getCustomPropertyValue({ custom_properties: newCustomProperties }, wallCustomPostProperty)
        const oldValue = getCustomPropertyValue({ custom_properties: oldCustomProperties }, wallCustomPostProperty)

        merged[propId] = {
          ...newValue,
          // preserve user_response if client already has it set!
          ...(oldValue?.user_response !== undefined && { user_response: oldValue.user_response }),
        }
      } else {
        const newValue = getCustomPropertyValue({ custom_properties: newCustomProperties }, wallCustomPostProperty)

        merged[propId] = newValue
      }
    })
    return merged
  }
  // #endregion

  return {
    createOrUpdateCustomPropertyResponse,
    deleteCustomPropertyResponse,
    // Realtime message handlers
    updateAggregatedCustomPropertyResponseRealtime,
    updateUserCustomPropertyResponseRealtime,
    deleteUserCustomPropertyResponseRealtime,
    mergeRealtimeCustomPropertiesPayload,
  }
}
export { useSurfaceCustomPropertyResponse }
